import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, LogOut, Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useLang, authTranslations } from "../i18n";
import { useAuth } from "../auth";
import { Logo, googleLogin } from "./Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

function formatAmount(cents, lang) {
  return ((cents || 0) / 100).toLocaleString(lang === "it" ? "it-IT" : "en-US", {
    style: "currency",
    currency: "EUR",
  });
}

function StatusChip({ status, a }) {
  const map = {
    paid: { label: a.statusPaid, cls: "bg-emerald-400/15 text-emerald-300" },
    pending: { label: a.statusPending, cls: "bg-amber-400/15 text-amber-300" },
    failed: { label: a.statusFailed, cls: "bg-red-400/15 text-red-300" },
    expired: { label: a.statusFailed, cls: "bg-red-400/15 text-red-300" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function DonorArea() {
  const { lang } = useLang();
  const a = authTranslations[lang];
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [donations, setDonations] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/donations/mine`, { withCredentials: true })
      .then((r) => setDonations(r.data.donations))
      .catch(() => setDonations([]));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const paid = (donations || []).filter((d) => d.payment_status === "paid");
  const total = paid.reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="donor-area">
      <header className="border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
          <button
            data-testid="donor-back-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft size={14} />
            {a.backHome}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
        {loading ? (
          <div className="flex justify-center py-32">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : !user ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mx-auto max-w-md text-center"
            data-testid="donor-login-prompt"
          >
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-primary/15 text-primary">
              <Heart size={28} />
            </span>
            <h1 className="mt-8 font-serif text-4xl tracking-tight text-white md:text-5xl">
              {a.loginPromptTitle}
            </h1>
            <p className="mt-4 text-base text-white/60">{a.loginPromptText}</p>
            <button
              data-testid="donor-login-button"
              onClick={googleLogin}
              className="mt-10 rounded-full bg-white px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#04122e] transition-all duration-300 hover:-translate-y-0.5"
            >
              {a.loginWithGoogle}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex flex-col gap-8 rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:flex-row md:items-center md:p-10">
              <div className="flex items-center gap-5">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-full border-2 border-primary/50 object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="grid h-20 w-20 place-items-center rounded-full bg-primary font-serif text-3xl text-white">
                    {(user.name || user.email || "U")[0].toUpperCase()}
                  </span>
                )}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary">
                    {a.member}
                  </div>
                  <h1 className="mt-1 font-serif text-3xl tracking-tight text-white md:text-4xl" data-testid="donor-name">
                    {user.name}
                  </h1>
                  <p className="mt-1 text-sm text-white/50" data-testid="donor-email">{user.email}</p>
                </div>
              </div>
              <div className="flex gap-10 md:ml-auto">
                <div>
                  <div className="font-serif text-3xl text-white md:text-4xl" data-testid="donor-total">
                    {formatAmount(total, lang)}
                  </div>
                  <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">
                    {a.totalDonated}
                  </div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-white md:text-4xl" data-testid="donor-count">
                    {paid.length}
                  </div>
                  <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">
                    {a.donationsCount}
                  </div>
                </div>
              </div>
              <button
                data-testid="donor-logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors hover:border-white/50 hover:text-white md:self-start"
              >
                <LogOut size={14} />
                {a.logout}
              </button>
            </div>

            <h2 className="mt-16 font-serif text-3xl tracking-tight text-white md:text-4xl">
              {a.myDonations}
            </h2>

            {donations === null ? (
              <div className="flex justify-center py-16">
                <Loader2 size={32} className="animate-spin text-primary" />
              </div>
            ) : donations.length === 0 ? (
              <div className="mt-8 rounded-3xl border border-dashed border-white/15 p-12 text-center" data-testid="donor-empty-state">
                <p className="text-white/55">{a.noDonations}</p>
                <button
                  data-testid="donor-first-donation-button"
                  onClick={() => navigate("/")}
                  className="mt-6 rounded-full bg-primary px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a]"
                >
                  {a.noDonationsCta}
                </button>
              </div>
            ) : (
              <div className="mt-8 border-t border-white/10" data-testid="donor-donations-list">
                {donations.map((d, i) => (
                  <div
                    key={d.session_id || i}
                    data-testid={`donation-row-${i}`}
                    className="flex flex-wrap items-center gap-4 border-b border-white/10 py-6"
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary">
                      <Heart size={18} />
                    </span>
                    <div className="font-serif text-2xl text-white">{formatAmount(d.amount, lang)}</div>
                    <div className="text-sm uppercase text-white/40">
                      {(d.currency || "eur").toUpperCase()}
                    </div>
                    <div className="text-sm text-white/50">
                      {d.created_at
                        ? new Date(d.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : ""}
                    </div>
                    <div className="ml-auto">
                      <StatusChip status={d.payment_status} a={a} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
